/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';

import { IPatient } from 'shared/interfaces/patient.interface';
import { IPaymentMethod } from 'shared/interfaces/payment-method.interface';
import { IQuestionnaireInstance } from 'shared/interfaces/questionnaire-instance.interface';
import { IState } from 'shared/interfaces/state.interface';
import {
  TInvitePatientCompleteQuestionnaireAction,
  TPaginationRequest,
  TUploadPatientFile
} from 'shared/types';
import { initialStore } from '../initial-store';

export const createPatientNote = createAction(
  'patients/CREATE_PATIENT_NOTE',
  (payload) => ({ payload })
);

export const deletePatientNote = createAction(
  'patients/DELETE_PATIENT_NOTE',
  (payload) => ({ payload })
);
export const createOrder = createAction('patients/CREATE_ORDER', (payload) => ({
  payload
}));

export const createStripeSetupIntent = createAction(
  'patients/CREATE_STRIPE_SETUP_INTENT',
  (payload) => ({ payload })
);

export const addPaymentMethod = createAction(
  'patients/ADD_PAYMENT_METHOD',
  (payload) => ({ payload })
);

export const getPaymentMethods = createAction(
  'patients/GET_PAYMENT_METHODS',
  (payload) => ({ payload })
);

export const removePaymentMethod = createAction(
  'patients/removePaymentMethod',
  (payload) => ({ payload })
);

export const addAddress = createAction('patients/ADD_ADDRESS', (payload) => ({
  payload
}));

export const getAddress = createAction('patients/GET_ADDRESS', (payload) => ({
  payload
}));

export const getOnePatient = createAction(
  'patients/GET_PATIENT',
  (payload) => ({
    payload
  })
);

export const uploadPatientFile = createAction(
  'patient/uploadPatientFile',
  (payload: TUploadPatientFile) => ({ payload })
);

// Slices

const patientsSlice = createSlice({
  name: 'patients',
  initialState: initialStore.patients,
  reducers: {
    setPatientsData: (state, { payload }) => {
      state.dataPatients = payload.result;
      state.count = payload.count;
      state.isLoading = false;
    },
    getPatientsData: (
      state,
      data: PayloadAction<TPaginationRequest | undefined>
    ) => {
      if (!state.isLoading) {
        state.isLoading = true;
      }
    },
    createPatientData: (state, { payload }) => {
      state.isLoading = true;
    },
    actionFail: (state) => {
      state.isLoading = false;
    },
    buildPatientPage: (state, { payload }) => {
      state.isLoading = true;
    },
    updatePatientData: (state, { payload }) => {
      if (!state.isLoading) {
        state.dataIndividualPatient.isLoadingUpdate = true;
      }
    },
    updatePatientDataSuccess: (state, { payload }) => {
      state.dataIndividualPatient.data = payload;
      state.dataIndividualPatient.isLoadingUpdate = false;
    },
    updatePatientDataFail: (state) => {
      state.dataIndividualPatient.isLoadingUpdate = false;
    },
    buildPatientPaymentmethod: (state) => {
      state.isLoading = true;
    },
    setIndividualPatientinfo: (state, { payload }) => {
      const [data, orders] = payload;
      state.dataIndividualPatient.data = {
        ...data,
        questionnaireInstances: data.questionnaireInstances.filter(
          (qi: IQuestionnaireInstance) => qi.questionnaire
        )
      };
      state.dataIndividualPatient.orders = orders.result;
      state.isLoading = false;
    },
    setPatient: (state, { payload }) => {
      state.dataIndividualPatient.data = {
        ...payload,
        questionnaireInstances: payload.questionnaireInstances.filter(
          (qi: IQuestionnaireInstance) => qi.questionnaire
        )
      };
      state.isLoading = false;
    },
    setIndividualPatientpaymentMethod: (state, { payload }) => {
      state.dataIndividualPatient.payment.paymentMethods = payload;
      state.isLoading = false;
    },
    createPatientOrderSuccess: (state, { payload }) => {
      state.dataIndividualPatient.orders = [
        payload,
        ...state.dataIndividualPatient.orders
      ];
      if (
        state.dataIndividualPatient.data &&
        payload.questionnaireInstances?.length
      ) {
        const patientData = state.dataIndividualPatient.data as IPatient;
        patientData.questionnaireInstances = [
          ...payload.questionnaireInstances.filter(
            (orderQI: IQuestionnaireInstance) =>
              !patientData.questionnaireInstances.some(
                (patientQI) => patientQI.id === orderQI.id
              )
          ),
          ...patientData.questionnaireInstances
        ];
      }
      if (state.dataIndividualPatient.data && !payload.isAddressInSelect) {
        state.dataIndividualPatient.data.addresses = [
          ...state.dataIndividualPatient.data.addresses,
          payload.address
        ];
      }
      state.isLoading = false;
    },
    addNewNote: (state, { payload }) => {
      if (state.dataIndividualPatient.data) {
        (state.dataIndividualPatient.data as IPatient).notes = (
          state.dataIndividualPatient.data as IPatient
        ).notes.concat(payload);
      }
    },
    removeNote: (state, { payload }) => {
      if (state.dataIndividualPatient.data) {
        (state.dataIndividualPatient.data as IPatient).notes = (
          state.dataIndividualPatient.data as IPatient
        ).notes.filter(({ id }) => id !== payload);
      }
    },
    setStripeSetupIntent: (state, { payload }) => {
      state.dataIndividualPatient.payment.clientSecret = payload.client_secret;
    },
    addNewCard: (state, { payload }) => {
      (state.dataIndividualPatient.payment.paymentMethods as IPaymentMethod[]) =
        [payload, ...state.dataIndividualPatient.payment.paymentMethods];
    },
    removePaymentMethodSuccess: (state, { payload }) => {
      state.dataIndividualPatient.payment.paymentMethods =
        state.dataIndividualPatient.payment.paymentMethods.filter(
          ({ paymentId }) => paymentId !== payload.paymentMethodId
        );
    },
    updateQuestionnaire: (state, { payload }) => {
      if (!state.isLoading) {
        state.dataIndividualPatient.isLoadingUpdate = true;
      }
    },
    invitePatientCompleteQuestionnaire: (
      state,
      data: PayloadAction<TInvitePatientCompleteQuestionnaireAction | undefined>
    ) => {
      state.isLoading = false;
    },
    setUpdatedQuestionnaireSuccess: (state, { payload }) => {
      state.isLoading = false;
    },
    updatedQuestionnaireError: (state) => {
      state.isLoading = false;
    },
    setNewAddress: (state, { payload }) => {
      if (state.dataIndividualPatient.data) {
        (state.dataIndividualPatient.data as IPatient).addresses = [
          ...payload.addresses
        ];
      }
      state.isLoading = false;
    },
    setPatientLabOrders: (state, { payload }) => {
      if (state.dataIndividualPatient.data) {
        (state.dataIndividualPatient.data as IPatient).labOrders = [
          ...(state.dataIndividualPatient.data as IPatient).labOrders,
          payload
        ];
      }
    },
    setPatientFiles: (state, { payload }) => {
      if (state?.dataIndividualPatient?.data) {
        const patientData = state.dataIndividualPatient.data as IPatient;
        patientData.files = [...(patientData.files || []), payload];
      }
    }
  }
});

export const {
  setPatientsData,
  getPatientsData,
  createPatientData,
  actionFail,
  addNewNote,
  removeNote,
  buildPatientPage,
  setIndividualPatientinfo,
  buildPatientPaymentmethod,
  setIndividualPatientpaymentMethod,
  createPatientOrderSuccess,
  updatePatientDataFail,
  addNewCard,
  setStripeSetupIntent,
  removePaymentMethodSuccess,
  updatePatientData,
  updatePatientDataSuccess,
  updateQuestionnaire,
  setUpdatedQuestionnaireSuccess,
  updatedQuestionnaireError,
  invitePatientCompleteQuestionnaire,
  setNewAddress,
  setPatient,
  setPatientLabOrders,
  setPatientFiles
} = patientsSlice.actions;

export const { reducer } = patientsSlice;

// Selectors
export const getPatientsSelector = (state: IState) =>
  state && state.patients.dataPatients;

export const getPatientsLoadingSelector = (state: IState) =>
  state && state.patients.isLoading;

export const getPatientsLoadingUpdateSelector = (state: IState) =>
  state && state.patients.dataIndividualPatient.isLoadingUpdate;

export const getPatientIndividualSelector = (state: IState) =>
  state && {
    data: state.patients.dataIndividualPatient.data,
    orders: state.patients.dataIndividualPatient.orders,
    payment: state.patients.dataIndividualPatient.payment
  };

export const getPatientCount = (state: IState) => state && state.patients.count;
